import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function LoginPage({ isSuperAdmin = false }) {
  const { t } = useTranslation();
  const { login, verify2FA } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.requires_2fa) {
        setRequires2FA(true);
        toast.info('2FA code sent to your email');
      } else {
        toast.success('Login successful!');
        
        // Redirect based on role and login type
        if (isSuperAdmin) {
          if (result.user.role === 'super_admin') {
            navigate('/admin');
          } else {
            toast.error('Access denied. Super admin credentials required.');
            return;
          }
        } else {
          const redirect = searchParams.get('redirect');
          if (redirect) {
            navigate(redirect);
          } else {
            // Redirect based on role
            switch (result.user.role) {
              case 'super_admin':
                navigate('/admin');
                break;
              case 'restaurant_admin':
                navigate('/restaurant');
                break;
              case 'driver':
                navigate('/driver');
                break;
              case 'sales_person':
                navigate('/sales');
                break;
              case 'waiter':
                navigate('/waiter');
                break;
              default:
                navigate('/');
            }
          }
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await verify2FA(email, twoFACode);
      toast.success('Login successful!');
      
      if (isSuperAdmin && result.user.role !== 'super_admin') {
        toast.error('Access denied. Super admin credentials required.');
        return;
      }
      
      // Redirect based on role
      switch (result.user.role) {
        case 'super_admin':
          navigate('/admin');
          break;
        case 'restaurant_admin':
          navigate('/restaurant');
          break;
        case 'driver':
          navigate('/driver');
          break;
        case 'sales_person':
          navigate('/sales');
          break;
        case 'waiter':
          navigate('/waiter');
          break;
        default:
          navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid 2FA code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{isSuperAdmin ? 'Admin Login - Hambre' : 'Login - Hambre'}</title>
        <meta name="description" content="Sign in to your Hambre account to order food and track deliveries." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className={`min-h-screen flex items-center justify-center px-4 py-12 ${isSuperAdmin ? 'bg-slate-900' : 'gradient-hero'}`}>
        <div className="w-full max-w-md">
          <div className={`rounded-3xl shadow-xl p-8 ${isSuperAdmin ? 'bg-slate-800 border border-slate-700' : 'bg-white shadow-orange-500/10'}`}>
            {/* Logo */}
            <div className="text-center mb-8">
              <Link to="/" className="inline-block">
                <svg viewBox="0 0 100 100" className="w-16 h-16 mx-auto">
                  <rect width="100" height="100" rx="20" fill={isSuperAdmin ? "#DC2626" : "#F97316"}/>
                  <g fill="white">
                    <rect x="18" y="25" width="4" height="50" rx="2"/>
                    <rect x="25" y="25" width="3" height="20" rx="1.5"/>
                    <rect x="18" y="25" width="3" height="20" rx="1.5"/>
                    <rect x="11" y="25" width="3" height="20" rx="1.5"/>
                  <ellipse cx="78" cy="35" rx="12" ry="15"/>
                  <rect x="76" y="45" width="4" height="30" rx="2"/>
                  <rect x="30" y="50" width="44" height="8" rx="4"/>
                </g>
              </svg>
            </Link>
            
            {isSuperAdmin && (
              <div className="flex items-center justify-center gap-2 mt-4 text-red-500">
                <Shield className="w-5 h-5" />
                <span className="font-semibold">Super Admin Access</span>
              </div>
            )}
            
            <h1 className={`text-2xl font-bold mt-4 ${isSuperAdmin ? 'text-white' : 'text-slate-900'}`}>
              {requires2FA ? t('auth.2fa') : (isSuperAdmin ? 'Admin Login' : t('auth.loginTitle'))}
            </h1>
            <p className={`mt-2 ${isSuperAdmin ? 'text-slate-400' : 'text-slate-500'}`}>
              {requires2FA ? t('auth.enterCode') : (isSuperAdmin ? 'Restricted access area' : t('auth.loginSubtitle'))}
            </p>
          </div>

          {requires2FA ? (
            <form onSubmit={handle2FASubmit} className="space-y-6">
              <div>
                <Label htmlFor="2fa-code" className={isSuperAdmin ? 'text-slate-300' : ''}>{t('auth.verificationCode')}</Label>
                <Input
                  id="2fa-code"
                  type="text"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  className={`mt-2 text-center text-2xl tracking-widest ${isSuperAdmin ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                  maxLength={6}
                  required
                  data-testid="2fa-code-input"
                />
              </div>
              <Button 
                type="submit" 
                className={`w-full ${isSuperAdmin ? 'bg-red-600 hover:bg-red-700 text-white' : 'btn-primary'}`}
                disabled={loading}
                data-testid="verify-2fa-button"
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className={`w-full ${isSuperAdmin ? 'text-slate-400 hover:text-white' : ''}`}
                onClick={() => setRequires2FA(false)}
              >
                Back to login
              </Button>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="email" className={isSuperAdmin ? 'text-slate-300' : ''}>{t('auth.email')}</Label>
                <div className="relative mt-2">
                  <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isSuperAdmin ? 'text-slate-500' : 'text-slate-400'}`} />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={isSuperAdmin ? "admin@hambre.app" : "you@example.com"}
                    className={`pl-10 ${isSuperAdmin ? 'bg-slate-700 border-slate-600 text-white placeholder:text-slate-500' : ''}`}
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center">
                  <Label htmlFor="password" className={isSuperAdmin ? 'text-slate-300' : ''}>{t('auth.password')}</Label>
                  {!isSuperAdmin && (
                    <Link to="/forgot-password" className="text-sm text-orange-500 hover:text-orange-600">
                      {t('auth.forgotPassword')}
                    </Link>
                  )}
                </div>
                <div className="relative mt-2">
                  <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isSuperAdmin ? 'text-slate-500' : 'text-slate-400'}`} />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className={`pl-10 pr-10 ${isSuperAdmin ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${isSuperAdmin ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className={`w-full ${isSuperAdmin ? 'bg-red-600 hover:bg-red-700 text-white' : 'btn-primary'}`}
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? 'Signing in...' : t('auth.login')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              {/* Google Sign In - Only for regular login, not super admin */}
              {!isSuperAdmin && (
                <div className="relative mt-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-slate-500">{t('auth.orContinueWith')}</span>
                  </div>
                </div>
              )}

              {!isSuperAdmin && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mt-4 border-slate-200 hover:bg-slate-50"
                  onClick={() => {
                    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                    const redirectUrl = window.location.origin + '/auth/callback';
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  data-testid="google-login-button"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {t('auth.continueWithGoogle')}
                </Button>
              )}
            </form>
          )}

          {!isSuperAdmin && (
            <div className="mt-6 text-center">
              <p className="text-slate-500">
                {t('auth.noAccount')}{' '}
                <Link to="/register" className="text-orange-500 font-medium hover:text-orange-600">
                  {t('auth.register')}
                </Link>
              </p>
            </div>
          )}
          
          {isSuperAdmin && (
            <div className="mt-6 text-center">
              <p className="text-slate-500 text-sm">
                Not a super admin?{' '}
                <Link to="/login" className="text-red-400 font-medium hover:text-red-300">
                  Go to regular login
                </Link>
              </p>
            </div>
          )}
          </div>
        </div>
      </div>
    </>
  );
}
